import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Modal, Box, Typography,
  Button, Grid, TextField, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, Stack, Card, CardHeader, CardContent, Badge,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Visibility as VisibilityIcon, Close as CloseIcon, Description as DescriptionIcon,
  Person as PersonIcon, Subject as SubjectIcon, Category as CategoryIcon,
  Email as EmailIcon, Phone as PhoneIcon, Home as HomeIcon, Assignment as AssignmentIcon,
  Note as NoteIcon, Today as TodayIcon, CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Search as SearchIcon,
  FilterAlt as FilterAltIcon, CalendarMonth as CalendarMonthIcon, 
  AccountCircle as AccountCircleIcon, Work as WorkIcon, Info as InfoIcon,
  FileDownload as FileDownloadIcon,
  Edit,Delete as DeleteIcon,
  PrintSharp
} from '@mui/icons-material';
import Link from '@mui/material/Link';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { hosturl } from '../libs/Constant';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import ViewFormModel from './ViewModel';

const FormList = () => {
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('applicantName');
  const [sections, setSections] = useState([]);
  const [receiveFroms, setReceiveFroms] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedReceiveFrom, setSelectedReceiveFrom] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState(null);
  const [toDateFilter, setToDateFilter] = useState(null);
  const [activeFilters, setActiveFilters] = useState(0);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'view' or 'edit'
  const [role, setRole] = useState('');
  const navigate = useNavigate();
 

useEffect(() => {
  const token = localStorage.getItem('token');
  const storedRole = localStorage.getItem('role');

  // Redirect if no token or role
  if (!token || !storedRole) {
    navigate('/');
    return;
  }

  setRole(storedRole); // Set the role

const fetchData = async () => {
  setLoading(true);

  try {
    const formsUrl = storedRole === 'user' ? '/user-form' : '/form';
    const formsRes = await fetch(`${hosturl}${formsUrl}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    const formsData = await formsRes.json();
    const formsResult = (formsData.result || []).sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    setForms(formsResult);
    setFilteredForms(formsResult);

    // Fetch sections and receiveFroms for all roles
    const [sectionsRes, receiveFromsRes] = await Promise.all([
      fetch(`${hosturl}/section`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }),
      fetch(`${hosturl}/receive-from`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }),
    ]);

    const sectionsData = await sectionsRes.json();
    const receiveFromsData = await receiveFromsRes.json();

    setSections(sectionsData.result || []);
    setReceiveFroms(receiveFromsData.result || []);

  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    setLoading(false);
  }
};


  fetchData();
}, [hosturl, navigate]);


  // Count active filters
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedSection) count++;
    if (selectedReceiveFrom) count++;
    if (fromDateFilter) count++;
    if (toDateFilter) count++;
    setActiveFilters(count);
  }, [searchTerm, selectedSection, selectedReceiveFrom, fromDateFilter, toDateFilter]);

  // Apply all filters whenever any filter changes
  useEffect(() => {
    let filtered = forms?.length>0?[...forms]: [];

    // Apply search filter
if (searchTerm) {
  const term = searchTerm.toLowerCase();
  filtered = filtered.filter(form => {
    const applicantName = form.applicantName?.toLowerCase() || '';
    const managementName = form.managementName?.toLowerCase() || '';
    const fileNumber = form.fileNumber?.toLowerCase() || '';
    const applicantMail = form.applicantMail?.toLowerCase() || '';

    return (
      applicantName.includes(term) ||
      managementName.includes(term) ||
      fileNumber.includes(term) ||
      applicantMail.includes(term)
    );
  });
}

    // Apply section filter
    if (selectedSection) {
      filtered = filtered.filter(form => form.section === selectedSection);
    }

    // Apply receive-from filter
    if (selectedReceiveFrom) {
      filtered = filtered.filter(form => form.receiveFrom === selectedReceiveFrom);
    }

    // Apply date range filter
    if (fromDateFilter || toDateFilter) {
      filtered = filtered.filter(form => {
        const hearingDate = new Date(form.nextDateOfHearing);
        const fromDate = fromDateFilter ? new Date(fromDateFilter) : null;
        const toDate = toDateFilter ? new Date(toDateFilter) : null;
        
        if (fromDate && toDate) {
          return hearingDate >= fromDate && hearingDate <= toDate;
        } else if (fromDate) {
          return hearingDate >= fromDate;
        } else if (toDate) {
          return hearingDate <= toDate;
        }
        return true;
      });
    }

    setFilteredForms(filtered);
  }, [searchTerm, searchField, selectedSection, selectedReceiveFrom, fromDateFilter, toDateFilter, forms]);

  const handleView = (form) => {
    setSelectedForm(form);
    setMode('view'); // Set mode to view
    setOpen(true); // Open the modal
  };
  const handleClose = () => {
    setOpen(false); // Close the modal/dialog
    setSelectedForm(null); // Clear selected form
    setMode(null); // Clear mode
  };
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedSection('');
    setSelectedReceiveFrom('');
    setFromDateFilter(null);
    setToDateFilter(null);
    setSearchField('applicantName');
  };
  
  const downloadExcel = () => {
    // Prepare data for Excel
      const excelData = filteredForms.map(form => ({
        'File Number': form.fileNumber,
        'Memo Number': form.memoNumber,
        'Applicant Name': form.applicantName,
        'Applicant Phone': form.applicantPhoneNumber,
        'Applicant Email': form.applicantMail,
        'Applicant Address': form.applicantAddress,
        'Management Phone': form.managementPhoneNumber,
        'Management Email': form.managementMail,
        'Management Address': form.managementAddress,
        'Section': form.section,
        'Receive From': form.receiveFrom,
        'Hearing Date': new Date(form.nextDateOfHearing).toLocaleDateString('en-GB'), // ✅ FIXED
        'Status': form.finalStatus,
        'Subject': form.subject,
        'Submission Date': new Date(form.receivedDate).toLocaleDateString('en-GB') // Optional: keep format consistent
      }));

  
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const columnWidths = [
      { wch: 15 },{ wch: 15 },{ wch: 20 },{ wch: 15 }, { wch: 25 }, { wch: 40 }, { wch: 15 }, { wch: 25 }, { wch: 40 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 15 },];
    worksheet['!cols'] = columnWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FormsData");
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Forms_${date}.xlsx`);
  };

  const handleDelete = async (formId) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this form?");
  if (!confirmDelete) return;

  try {
    const res = await fetch(`${hosturl}/form/${formId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || 'Failed to delete form');
    }

    const updatedForms = forms.filter(form => form._id !== formId);
    setForms(updatedForms);
    setFilteredForms(updatedForms);

    alert('Form deleted successfully.');
  } catch (err) {
    console.error('Error deleting form:', err.message);
    alert('Failed to delete form.');
  }
};


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, background: '#f9fafb', minHeight: '100vh' }}>
        {/* Header Card */}
        <Card elevation={4} sx={{ 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1976d2 0%, #303f9f 100%)',
          color: 'white'
        }}>
          <CardHeader
            title={
              <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
                Forms Management System
              </Typography>
            }
            subheader={
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                View and manage all application forms
              </Typography>
            }
            action={
              <Stack direction="row" spacing={2}>
                <Badge badgeContent={activeFilters} color="secondary" showZero={false}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleResetFilters}
                    startIcon={<CloseIcon />}
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: 3,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    Reset Filters
                  </Button>
                </Badge>
                <Button
                  variant="contained"
                  color="success"
                  onClick={downloadExcel}
                  startIcon={<FileDownloadIcon />}
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: 3,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                >
                  Export Excel
                </Button>
              </Stack>
            }
          />
        </Card>

        {/* Filters Card */}
        <Card elevation={2} sx={{ mb: 3, borderRadius: 3 }}>
          <CardHeader
            avatar={<FilterAltIcon color="primary" sx={{ fontSize: '2rem' }} />}
            title={<Typography variant="h6">Advanced Filters</Typography>}
            subheader={
              <Typography variant="body2">
                {activeFilters > 0 ? `${activeFilters} active filter(s)` : 'No filters applied'}
              </Typography>
            }
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          />
          <CardContent>
            <Grid container spacing={2}>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search by file number, managementname,name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 , fontFamily:'poppins', fontSize:'15px'}
                }}
              />
            </Grid>


              {/* Section Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="section-filter-label">Section</InputLabel>
                  <Select
                    labelId="section-filter-label"
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    label="Section"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>All Sections</em>
                    </MenuItem>
                    {sections.map((section) => (
                      <MenuItem key={section._id} value={section.name}>
                        {section.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Receive From Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="receive-from-filter-label">Receive From</InputLabel>
                  <Select
                    labelId="receive-from-filter-label"
                    value={selectedReceiveFrom}
                    onChange={(e) => setSelectedReceiveFrom(e.target.value)}
                    label="Receive From"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>All Sources</em>
                    </MenuItem>
                    {receiveFroms.map((source) => (
                      <MenuItem key={source._id} value={source.name}>
                        {source.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range Filters */}
              <Grid item xs={12} sm={6} md={3} >
                <DatePicker
                  label="From Hearing Date"
                  value={fromDateFilter}
                  onChange={(newValue) => setFromDateFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 2 }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Hearing Date"
                  value={toDateFilter}
                  onChange={(newValue) => setToDateFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 2 }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(to right, #1976d2, #2196f3)',
                  '& th': { 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                  }
                }}>
                  <TableCell>#</TableCell>
                  <TableCell>File Number</TableCell>
                  <TableCell>Applicant Name</TableCell>
                  <TableCell>Managment Name</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Url</TableCell>
                  <TableCell>Hearing Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredForms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No forms found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredForms.map((form, index) => (
                    <TableRow 
                      key={form._id} 
                      hover 
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: 'action.hover' },
                        '&:last-child td': { border: 0 }
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <AssignmentIcon color="primary" fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {form.fileNumber}
                          </Typography>
                        </Stack>
                      </TableCell>
                         <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon color="primary" fontSize="small" />
                          <Typography variant="body2">
                            {form.applicantName}
                          </Typography>
                        </Stack>
                      </TableCell>
                       <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon color="primary" fontSize="small" />
                          <Typography variant="body2">
                            {form.managementName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CategoryIcon color="primary" fontSize="small" />
                          <Typography variant="body2">
                            {form.section}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CategoryIcon color="primary" fontSize="small" />
                    <Link
                      href={form.url.startsWith('http') ? form.url : `https://${form.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                    >
                      {form.url.length > 25 ? `${form.url.slice(0, 25)}...` : form.url}
                    </Link>
                  </Stack>
                </TableCell>
              <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon color="primary" fontSize="small" />
                    <Typography variant="body2">
                      {new Date(form.nextDateOfHearing).toLocaleDateString('en-GB')}
                    </Typography>
                  </Stack>
                </TableCell>

                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton 
                            onClick={() => handleView(form)}
                            color="primary"
                            aria-label="view details"
                            sx={{
                              backgroundColor: 'primary.light',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              }
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                           {/* <IconButton 
                          onClick={() => handleDelete(form._id)}
                          color="error"
                          aria-label="delete"
                          sx={{
                            backgroundColor: 'error.light',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'error.main',
                              color: 'white',
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton> */}

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}

              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Enhanced Modal */}
      <Modal open={open && mode === 'view'} onClose={handleClose}>
        <ViewFormModel formData={selectedForm} onClose={handleClose} />
      </Modal>
      </Box>
    </LocalizationProvider>
  );
};

export default FormList;